import React, { useMemo, useState } from "react";
import moment from "moment";
import classNames from "classnames";
import * as Icons from "Components/Icons";
import {
   CustomInput,
   Dropdown,
   DropdownMenu,
   DropdownToggle,
} from "Components/ReactstrapSlim";
import { DatePickerInput } from "Components/Inputs";
import { formatStringDate, toDate, toISOStringUnspecified } from "Utils/date";
import { useActivity } from "../hooks";
import { DatePickerInputProps } from "Components/Inputs/DatePickerInput";
import classes from "./DateFilter.module.scss";

const DATE_FORMAT = "DD MMM YYYY";

interface DateFilterProps { 
   className?: string
}

enum ActivityDate {
   Week = "week",
   Month = "month",
   Custom = "custom",
}

const DateFilter: React.FC<DateFilterProps> = ({ className }) => {
   const { filter, setFilter } = useActivity();
   const [open, setOpen] = useState(false);
   const [dateType, setDateType] = useState<string>(ActivityDate.Custom);
   const [startDate, setStartDate] = useState(() => toDate(filter.startDate));
   const [endDate, setEndDate] = useState(() => toDate(filter.endDate));

   const displayDate = useMemo(() => {
      const [startDay, startMonth, startYear] = formatStringDate(
         filter.startDate,
         DATE_FORMAT
      ).split(" ");
      const [endDay, endMonth, endYear] = formatStringDate(
         filter.endDate,
         DATE_FORMAT
      ).split(" ");

      if (startMonth === endMonth && startYear === endYear) {
         return `${startMonth} ${startDay}-${endDay}, ${startYear}`;
      }

      if (startYear === endYear) {
         return `${startDay} ${startMonth} - ${endDay} ${endMonth}, ${startYear}`;
      }

      return `${startDay} ${startMonth} ${startYear} - ${endDay} ${endMonth} ${endYear}`;
   }, [startDate, endDate]);


   const toggleOpen = () => setOpen((o) => !o);

   const onChange: DatePickerInputProps["onChange"] = (dates) => {
      if (!dates || !Array.isArray(dates)) {
         return;
      }

      const [start, end] = dates;
      setStartDate(start);
      setEndDate(end);

      if (start && end) {
         setDateType(ActivityDate.Custom);
         setFilter({
            startDate: toISOStringUnspecified(moment(start).startOf("day")),
            endDate: toISOStringUnspecified(moment(end).endOf("day")),
         });
         toggleOpen();
      }
   };

   const handleNextWeek = () => {
      const startOfWeek = moment().add(1, "week").startOf("week").startOf("day");
      const endOfWeek = startOfWeek.clone().endOf("week").endOf("day");

      setDateType(ActivityDate.Week);
      setStartDate(startOfWeek.toDate());
      setEndDate(endOfWeek.toDate());
      setFilter({
         startDate: toISOStringUnspecified(startOfWeek),
         endDate: toISOStringUnspecified(endOfWeek),
      });
      toggleOpen();
   };

   const handleNextMonths = () => {
      const startOfMonth = moment().add(1, "month").startOf("month").startOf("day");
      const endOfMonth = startOfMonth.clone().endOf("month").endOf("day");
      setDateType(ActivityDate.Month);
      setStartDate(startOfMonth.toDate());
      setEndDate(endOfMonth.toDate());
      setFilter({
         startDate: toISOStringUnspecified(startOfMonth),
         endDate: toISOStringUnspecified(endOfMonth),
      });
      toggleOpen();
   };

   return (
      <Dropdown isOpen={open} toggle={toggleOpen} direction="down">
         <DropdownToggle tag="div" className={classNames(classes.filterBadge, className)}>
            <Icons.Calendar className={classes.icon} />
            <div>{displayDate}</div>
         </DropdownToggle>
         <DropdownMenu className={classes.menu} modifiers={{
            flip: {
               enabled: false
            }
         }}>
            <CustomInput
               id={ActivityDate.Week}
               checked={dateType === ActivityDate.Week}
               value={ActivityDate.Week}
               onChange={handleNextWeek}
               type="radio"
               className={classes.radio}
               label="Next week"
            />
            <CustomInput
               id={ActivityDate.Month}
               checked={dateType === ActivityDate.Month}
               value={ActivityDate.Month}
               onChange={handleNextMonths}
               type="radio"
               className={classes.radio}
               label="Next month"
            />
            <div className={classes.separator} />
            <DatePickerInput
               name="date-picker"
               onChange={onChange}
               selected={startDate}
               startDate={startDate}
               endDate={endDate}
               calendarClassName={classes.datePicker}
               inline
               selectsRange
            />
         </DropdownMenu>
      </Dropdown>
   );
};

export default DateFilter;
